


import { useEffect, useState } from "react";
import "./formSlotRadio.css";
import propTypes from "prop-types";

// timeUtils.js
export const getRemainingTime = (endTime) => {
  if (!endTime) return "";

  const now = new Date();
  const end = new Date(endTime);

  let diffMs = end - now;

  if (diffMs <= 0) return "Free now";

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours} hr ${minutes} min`;
  }

  return `${minutes} min`;
};

// useLiveTime.js
const useLiveTime = (endTime) => {
  const [timeLeft, setTimeLeft] = useState(
    getRemainingTime(endTime)
  );

  useEffect(() => {
    if (!endTime) return;

    setTimeLeft(getRemainingTime(endTime));

    const interval = setInterval(() => {
      setTimeLeft(getRemainingTime(endTime));
    }, 60000); // update every 1 minute

    return () => clearInterval(interval);
  }, [endTime]);

  return timeLeft;
};

// SlotTimer.jsx
const SlotTimer = ({ endTime }) => {
  const timeLeft = useLiveTime(endTime);

  return (
    <small className="slot-timer text-muted" style={{ fontSize: "10px" }}>
      {timeLeft === "Free now"
        ? "Free now"
        : `Free after ${timeLeft}`}
    </small>
  );
};

const FormSlotRadio = ({
  field = {},
  value = "",
  handleChange = () => {},
  options = [],
  error = null,
  labelHide = false,
  className = "",
  multiSelect = false,
  maxSelections = null
}) => {
  const slotOptions = options.length ? options : field.options || [];

  // Parse value to always get array in multi-select mode
  const getSelectedValues = () => {
    if (multiSelect) {
      if (Array.isArray(value)) return value.map(String);
      if (typeof value === 'string' && value) {
        return value.split(',').map(v => v.trim());
      }
      return [];
    }
    return value;
  };

  const selectedValues = multiSelect ? getSelectedValues() : null;

  // Handle slot selection
  const handleSlotChange = (slotValue) => {
    if (multiSelect) {
      const currentSelections = getSelectedValues();
      let newSelections;

      if (currentSelections.includes(slotValue)) {
        // Remove if already selected
        newSelections = currentSelections.filter(v => v !== slotValue);
      } else {
        // Add if not selected
        if (maxSelections && currentSelections.length >= maxSelections) {
          // Replace last selection if max limit reached
          newSelections = [...currentSelections.slice(0, -1), slotValue];
        } else {
          newSelections = [...currentSelections, slotValue];
        }
      }

      // Convert string values to numbers for API
      const arrayValue = newSelections.map(v => Number(v));

      // Create synthetic event with array value
      const syntheticEvent = {
        target: {
          name: field.name,
          value: arrayValue, // Store as array [2, 5]
          type: 'checkbox'
        }
      };
      handleChange(syntheticEvent);
    } else {
      // Single select mode (original behavior)
      const syntheticEvent = {
        target: {
          name: field.name,
          value: slotValue,
          type: 'radio'
        }
      };
      handleChange(syntheticEvent);
    }
  };

  // Check if slot is selected
  const isSelected = (slotValue) => {
    if (multiSelect) {
      return selectedValues.includes(slotValue);
    }
    return value === slotValue;
  };

  return (
    <div className={`${className} ${field.fieldSize || "col-12"}`}>
      <div className="border p-3 rounded-4 text-center shadow-sm border-0">

        {!labelHide && field.label && (
          <label className="form-label text-muted">
            {field.label}
            {field.required && (
              <span className="text-danger small"> *</span>
            )}

          </label>
        )}
        <hr className="mb-3 border mt-0" />

        <div className="slot-radio-wrapper">
          {slotOptions.map((slot) => {
            const selected = isSelected(slot.value);

            return (
              <label
                key={slot.value}
                className={`slot-radio-card
                  ${selected ? "active" : ""}
                  ${slot.disabled ? "disabled" : ""}
                  ${error ? "border-danger" : ""}
                `}
              >
                <input
                  type={multiSelect ? "checkbox" : "radio"}
                  name={field.name}
                  value={slot.value}
                  checked={selected}
                  disabled={slot.disabled}
                  onChange={() => handleSlotChange(slot.value)}
                />

                <span className="slot-check">✔</span>
                <span className="slot-icon">{slot.icon || "🚗"}</span>
                <span className="slot-label">{slot.label}</span>

                {/* STATUS */}
                {slot.status === "available" && (
                  <small className="slot-status available">
                    Available
                  </small>
                )}

                {slot.status === "booked" && (
                  <>
                    <small className="slot-status booked">
                      Booked
                    </small>

                    {/* LIVE TIMER */}
                    <SlotTimer endTime={slot.Book_out} />
                  </>
                )}
              </label>
            );
          })}
        </div>

        {error && (
          <span className="text-danger small">{error}</span>
        )}
      </div>
    </div>
  );
};

export default FormSlotRadio;



